import { numberToPercentWithFixedFractionDigits } from '../helpers/number';
import { DjangoTimeStamped, TimeStamped } from './../django';
import { DjangoEmployer, Employer, parseEmployer } from './../employer';
import { applyTimezone, getDateOnly, parseDate } from './../helpers/date';
import { EMPLOYER_CREDIT_POLICY_TYPE } from './employer-credit-policy-type.enum';

export class EmployerCreditPolicy implements TimeStamped {
  id: string;
  originationFee: string;
  isOriginationFeePercentage: boolean;
  minimumMonthsSinceHiring: number;
  maximumInstallmentAmountPercentage: number;
  minimumInstallmentsCount: number;
  maximumInstallmentsCount: number;
  maximumSalaryMultiplier: string;
  minimumInterestRate: number;
  maximumInterestRate: number;
  policyType: EMPLOYER_CREDIT_POLICY_TYPE;
  additionalConditions: any;
  negativeScore: boolean;
  employer: Employer;
  minimumFirstDueDate: Date;
  maximumAmount: number;
  minimumAmount: number;
  freeRequestLimit: number;
  maximumOriginationFee: number;
  feeAdditionForNegatives: number;
  specialOriginationFee: number;
  maxAmountForSpecialFee: number;
  maximumProposalCount: number;
  loanInsurancePremiumRate: number;
  loanInsurancePremiumLimitAmount: number;
  // DJANGO TIMESTAMPED
  created: Date;
  modified: Date;

  constructor(data: string | EmployerCreditPolicy | DjangoEmployerCreditPolicy = {}) {
    if (data instanceof EmployerCreditPolicy) {
      bindEmployerCreditPolicyData(this, data);
    } else if (typeof data === 'string') {
      bindDjangoEmployerCreditPolicyData(this, { id: data });
    } else {
      bindDjangoEmployerCreditPolicyData(this, data);
    }
  }

  get djangoModel() {
    return getDjangoModel(this);
  }
}

export function parseEmployerCreditPolicy(val) {
  if (val) {
    return new EmployerCreditPolicy(val);
  } else {
    return undefined;
  }
}

function bindEmployerCreditPolicyData(employerCreditPolicy, data) {
  employerCreditPolicy.id = data.id;
  employerCreditPolicy.originationFee = data.originationFee;
  employerCreditPolicy.isOriginationFeePercentage = data.isOriginationFeePercentage;
  employerCreditPolicy.minimumMonthsSinceHiring = data.minimumMonthsSinceHiring;
  employerCreditPolicy.maximumInstallmentAmountPercentage = data.maximumInstallmentAmountPercentage;
  employerCreditPolicy.minimumInstallmentsCount = data.minimumInstallmentsCount;
  employerCreditPolicy.maximumInstallmentsCount = data.maximumInstallmentsCount;
  employerCreditPolicy.maximumSalaryMultiplier = data.maximumSalaryMultiplier;
  employerCreditPolicy.minimumInterestRate = data.minimumInterestRate;
  employerCreditPolicy.maximumInterestRate = data.maximumInterestRate;
  employerCreditPolicy.policyType = data.policyType;
  employerCreditPolicy.additionalConditions = data.additionalConditions;
  employerCreditPolicy.negativeScore = data.negativeScore;
  employerCreditPolicy.employer = data.employer;
  employerCreditPolicy.minimumFirstDueDate = data.minimumFirstDueDate;
  employerCreditPolicy.maximumAmount = data.maximumAmount;
  employerCreditPolicy.minimumAmount = data.minimumAmount;
  employerCreditPolicy.freeRequestLimit = data.freeRequestLimit;
  employerCreditPolicy.maximumOriginationFee = data.maximumOriginationFee;
  employerCreditPolicy.feeAdditionForNegatives = data.feeAdditionForNegatives;
  employerCreditPolicy.specialOriginationFee = data.specialOriginationFee;
  employerCreditPolicy.maxAmountForSpecialFee = data.maxAmountForSpecialFee;
  employerCreditPolicy.maximumProposalCount = data.maxmimumProposalCount;
  employerCreditPolicy.loanInsurancePremiumRate = data.loanInsurancePremiumRate
  employerCreditPolicy.loanInsurancePremiumLimitAmount = data.loanInsurancePremiumLimitAmount

  // DJANGO TIMESTAMPED
  employerCreditPolicy.created = data.created;
  employerCreditPolicy.modified = data.modified;
}

///////////////////
// DJANGO SUPPOT //
///////////////////
export interface DjangoEmployerCreditPolicy extends DjangoTimeStamped {
  id?: string;
  origination_fee?: string;
  is_origination_fee_percentage?: boolean;
  minimum_months_since_hiring?: number;
  maximum_installment_amount_percentage?: string;
  minimum_installments_count?: number;
  maximum_installments_count?: number;
  maximum_salary_multiplier?: string;
  minimum_interest_rate?: string;
  maximum_interest_rate?: string;
  policy_type?: EMPLOYER_CREDIT_POLICY_TYPE;
  additional_conditions?: any;
  negative_score?: boolean;
  employer?: DjangoEmployer | string;
  minimum_first_due_date?: string;
  maximum_amount?: number;
  minimum_amount?: number;
  free_request_limit?: number;
  maximum_origination_fee?: number | null;
  fee_addition_for_negatives?: number;
  special_origination_fee?: number;
  max_amount_for_special_fee?: number;
  maximum_proposal_count?: number;
  loan_insurance_premium_rate?: number;
  loan_insurance_premium_limit_amount?: number;
  // DJANGO TIMESTAMPED
  created?: string;
  modified?: string;
}

function bindDjangoEmployerCreditPolicyData(employerCreditPolicy, data) {
  employerCreditPolicy.id = data.id;
  employerCreditPolicy.originationFee = data.origination_fee;
  employerCreditPolicy.isOriginationFeePercentage = data.is_origination_fee_percentage;
  employerCreditPolicy.minimumMonthsSinceHiring = data.minimum_months_since_hiring;
  employerCreditPolicy.maximumInstallmentAmountPercentage = (
    parseFloat(data.maximum_installment_amount_percentage || '0') * 100
  ).toFixed(2);
  employerCreditPolicy.minimumInstallmentsCount = data.minimum_installments_count;
  employerCreditPolicy.maximumInstallmentsCount = data.maximum_installments_count;
  employerCreditPolicy.maximumSalaryMultiplier = data.maximum_salary_multiplier;
  employerCreditPolicy.minimumInterestRate = (
    parseFloat(data.minimum_interest_rate || '0') * 100
  ).toFixed(2);
  employerCreditPolicy.maximumInterestRate = (
    parseFloat(data.maximum_interest_rate || '0') * 100
  ).toFixed(2);
  employerCreditPolicy.policyType = data.policy_type;
  employerCreditPolicy.additionalConditions = data.additional_conditions;
  employerCreditPolicy.negativeScore = data.negative_score;
  employerCreditPolicy.employer = parseEmployer(data.employer);
  employerCreditPolicy.minimumFirstDueDate = applyTimezone(
    parseDate(data.minimum_first_due_date),
    0,
  );
  employerCreditPolicy.maximumAmount = data.maximum_amount;
  employerCreditPolicy.minimumAmount = data.minimum_amount;
  employerCreditPolicy.freeRequestLimit = data.free_request_limit;
  employerCreditPolicy.maximumOriginationFee = data.maximum_origination_fee || null;
  employerCreditPolicy.feeAdditionForNegatives = data.fee_addition_for_negatives;
  employerCreditPolicy.specialOriginationFee = data.special_origination_fee;
  employerCreditPolicy.maxAmountForSpecialFee = data.max_amount_for_special_fee;
  employerCreditPolicy.maximumProposalCount = data.maximum_proposal_count;
  employerCreditPolicy.loanInsurancePremiumRate = data.loan_insurance_premium_rate;
  employerCreditPolicy.loanInsurancePremiumLimitAmount = data.loan_insurance_premium_limit_amount;
  // DJANGO TIMESTAMPED
  employerCreditPolicy.created = parseDate(data.created);
  employerCreditPolicy.modified = parseDate(data.modified);
}

function getDjangoModel(employerCreditPolicy: EmployerCreditPolicy) {
  return {
    id: employerCreditPolicy.id,
    origination_fee: employerCreditPolicy.originationFee,
    is_origination_fee_percentage: employerCreditPolicy.isOriginationFeePercentage,
    minimum_months_since_hiring: employerCreditPolicy.minimumMonthsSinceHiring,
    maximum_installment_amount_percentage: numberToPercentWithFixedFractionDigits(
      employerCreditPolicy.maximumInstallmentAmountPercentage,
      2,
    ),
    minimum_installments_count: employerCreditPolicy.minimumInstallmentsCount,
    maximum_installments_count: employerCreditPolicy.maximumInstallmentsCount,
    maximum_salary_multiplier: employerCreditPolicy.maximumSalaryMultiplier,
    minimum_interest_rate: numberToPercentWithFixedFractionDigits(
      employerCreditPolicy.minimumInterestRate,
      4,
    ),
    maximum_interest_rate: numberToPercentWithFixedFractionDigits(
      employerCreditPolicy.maximumInterestRate,
      4,
    ),
    policy_type: employerCreditPolicy.policyType,
    additional_conditions: employerCreditPolicy.additionalConditions,
    negative_score: employerCreditPolicy.negativeScore,
    employer: employerCreditPolicy.employer.id,
    minimum_first_due_date: getDateOnly(employerCreditPolicy.minimumFirstDueDate) || null,
    maximum_amount: employerCreditPolicy.maximumAmount,
    minimum_amount: employerCreditPolicy.minimumAmount,
    free_request_limit: employerCreditPolicy.freeRequestLimit,
    maximum_origination_fee: employerCreditPolicy.maximumOriginationFee || null,
    fee_addition_for_negatives: employerCreditPolicy.feeAdditionForNegatives,
    special_origination_fee: employerCreditPolicy.specialOriginationFee,
    max_amount_for_special_fee: employerCreditPolicy.maxAmountForSpecialFee,
    maximum_proposal_count: employerCreditPolicy.maximumProposalCount,
    loan_insurance_premium_rate: employerCreditPolicy.loanInsurancePremiumRate,
    loan_insurance_premium_limit_amount: employerCreditPolicy.loanInsurancePremiumLimitAmount,
  };
}
